/**
 * Компонент для отображения статистики LLM
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronRight, BarChart3, Loader2, AlertCircle } from 'lucide-react';
import { llmStatsService, LLMStats } from '../services/llmStatsService';
import { cn } from '../utils/cn';

interface LLMStatsDisplayProps {
  taskIds: string[];
  className?: string;
  theme?: 'dark' | 'light';
}

interface AggregatedStats {
  totalTaskIds: number;
  totalRequests: number;
  totalTokens: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalInputCost: number;
  totalOutputCost: number;
  totalCost: number;
  avgTokensPerRequest: number;
  avgCostPerRequest: number;
  modelStats: Record<string, {
    count: number;
    totalTokens: number;
    totalInputTokens: number;
    totalOutputTokens: number;
    totalInputCost: number;
    totalOutputCost: number;
    totalCost: number;
  }>;
  operationStats: Record<string, {
    count: number;
    totalTokens: number;
    totalInputTokens: number;
    totalOutputTokens: number;
    totalInputCost: number;
    totalOutputCost: number;
    totalCost: number;
  }>;
  taskStats: LLMStats[];
}

export const LLMStatsDisplay: React.FC<LLMStatsDisplayProps> = ({ 
  taskIds, 
  className,
  theme = 'light'
}) => {
  const isDark = theme === 'dark';
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [stats, setStats] = useState<AggregatedStats | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleToggle = async () => {
    if (!isExpanded && !stats && !isLoading) {
      // Загружаем статистику при первом раскрытии
      await loadStats();
    }
    setIsExpanded(!isExpanded);
  };

  const loadStats = async () => {
    if (taskIds.length === 0) {
      setError('Нет данных для отображения статистики');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const aggregatedStats = await llmStatsService.getAggregatedStats(taskIds);
      setStats(aggregatedStats);
    } catch (err) {
      console.error('Ошибка при загрузке статистики LLM:', err);
      setError('Не удалось загрузить статистику');
    } finally {
      setIsLoading(false);
    }
  };

  const formatCost = (cost: number) => llmStatsService.formatCost(cost);
  const formatTokens = (tokens: number) => llmStatsService.formatTokens(tokens);

  if (taskIds.length === 0) {
    return null; // Не показываем компонент, если нет taskId
  }

  return (
    <div className={cn(
      "border rounded-lg",
      isDark 
        ? "border-slate-600 bg-slate-800/50" 
        : "border-gray-200 bg-white",
      className
    )}>
      <button
        onClick={handleToggle}
        className={cn(
          "w-full px-3 py-2 text-left text-sm flex items-center justify-between transition-colors",
          isDark
            ? "text-slate-300 hover:text-white hover:bg-slate-700/50"
            : "text-gray-600 hover:text-gray-800 hover:bg-gray-50"
        )}
        disabled={isLoading}
      >
        <div className="flex items-center gap-2">
          <BarChart3 className="h-4 w-4" />
          <span>Статистика LLM</span>
          {taskIds.length > 1 && (
            <span className={cn(
              "text-xs px-2 py-0.5 rounded",
              isDark
                ? "bg-slate-600 text-slate-200"
                : "bg-gray-100 text-gray-700"
            )}>
              {taskIds.length} задач
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
          {isExpanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </div>
      </button>

      {isExpanded && (
        <div className={cn(
          "border-t p-3",
          isDark
            ? "border-slate-600 bg-slate-700/30"
            : "border-gray-200 bg-gray-50"
        )}>
          {error ? (
            <div className="flex items-center gap-2 text-red-600 text-sm">
              <AlertCircle className="h-4 w-4" />
              <span>{error}</span>
            </div>
          ) : stats ? (
            <div className="space-y-4">
              {/* Общая статистика */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                <div className="bg-white p-2 rounded border">
                  <div className="text-gray-500 text-xs">Запросов</div>
                  <div className="font-medium">{stats.totalRequests}</div>
                </div>
                <div className="bg-white p-2 rounded border">
                  <div className="text-gray-500 text-xs">Стоимость</div>
                  <div className="font-medium">{formatCost(stats.totalCost)}</div>
                </div>
                <div className="bg-white p-2 rounded border">
                  <div className="text-gray-500 text-xs">Среднее</div>
                  <div className="font-medium">{formatCost(stats.avgCostPerRequest)}</div>
                </div>
              </div>

              {/* Разделение токенов */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                <div className="bg-white p-2 rounded border">
                  <div className="text-gray-500 text-xs">Всего токенов</div>
                  <div className="font-medium">{formatTokens(stats.totalTokens)}</div>
                </div>
                <div className="bg-white p-2 rounded border">
                  <div className="text-gray-500 text-xs">Входные токены</div>
                  <div className="font-medium text-blue-600">{formatTokens(stats.totalInputTokens)}</div>
                </div>
                <div className="bg-white p-2 rounded border">
                  <div className="text-gray-500 text-xs">Выходные токены</div>
                  <div className="font-medium text-green-600">{formatTokens(stats.totalOutputTokens)}</div>
                </div>
              </div>

              {/* Разделение стоимости токенов */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                <div className="bg-white p-2 rounded border">
                  <div className="text-gray-500 text-xs">Общая стоимость</div>
                  <div className="font-medium">{formatCost(stats.totalCost)}</div>
                </div>
                <div className="bg-white p-2 rounded border">
                  <div className="text-gray-500 text-xs">Стоимость входных</div>
                  <div className="font-medium text-blue-600">{formatCost(stats.totalInputCost)}</div>
                </div>
                <div className="bg-white p-2 rounded border">
                  <div className="text-gray-500 text-xs">Стоимость выходных</div>
                  <div className="font-medium text-green-600">{formatCost(stats.totalOutputCost)}</div>
                </div>
              </div>

              {/* Статистика по моделям */}
              {Object.keys(stats.modelStats).length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Модели</h4>
                  <div className="space-y-1">
                    {Object.entries(stats.modelStats).map(([model, modelStats]) => (
                      <div key={model} className="bg-white p-2 rounded border">
                        <div className="flex justify-between items-center mb-1">
                          <span className="font-mono text-xs font-medium">{model}</span>
                          <div className="flex gap-3 text-xs text-gray-600">
                            <span>{modelStats.count} запр.</span>
                            <span className="font-medium">{formatCost(modelStats.totalCost)}</span>
                          </div>
                        </div>
                        <div className="flex gap-4 text-xs text-gray-500">
                          <span>Всего: {formatTokens(modelStats.totalTokens)}</span>
                          <span className="text-blue-600">Вход: {formatTokens(modelStats.totalInputTokens)}</span>
                          <span className="text-green-600">Выход: {formatTokens(modelStats.totalOutputTokens)}</span>
                        </div>
                        <div className="flex gap-4 text-xs text-gray-500 mt-1">
                          <span>Стоимость: {formatCost(modelStats.totalCost)}</span>
                          <span className="text-blue-600">Вход: {formatCost(modelStats.totalInputCost)}</span>
                          <span className="text-green-600">Выход: {formatCost(modelStats.totalOutputCost)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Статистика по операциям */}
              {Object.keys(stats.operationStats).length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Операции</h4>
                  <div className="space-y-1">
                    {Object.entries(stats.operationStats)
                      .sort(([,a], [,b]) => b.totalCost - a.totalCost) // Сортируем по стоимости (убывание)
                      .map(([operation, operationStats]) => (
                      <div key={operation} className="bg-white p-2 rounded border">
                        <div className="flex justify-between items-center mb-1">
                          <span className="font-medium capitalize">{operation.replace(/_/g, ' ')}</span>
                          <div className="flex gap-3 text-xs text-gray-600">
                            <span>{operationStats.count} запр.</span>
                            <span className="font-medium">{formatCost(operationStats.totalCost)}</span>
                          </div>
                        </div>
                        <div className="flex gap-4 text-xs text-gray-500">
                          <span>Всего: {formatTokens(operationStats.totalTokens)}</span>
                          <span className="text-blue-600">Вход: {formatTokens(operationStats.totalInputTokens)}</span>
                          <span className="text-green-600">Выход: {formatTokens(operationStats.totalOutputTokens)}</span>
                        </div>
                        <div className="flex gap-4 text-xs text-gray-500 mt-1">
                          <span>Стоимость: {formatCost(operationStats.totalCost)}</span>
                          <span className="text-blue-600">Вход: {formatCost(operationStats.totalInputCost)}</span>
                          <span className="text-green-600">Выход: {formatCost(operationStats.totalOutputCost)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Детальная статистика по задачам */}
              {stats.taskStats.length > 1 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Детали по задачам</h4>
                  <div className="space-y-2">
                    {stats.taskStats.map((task, index) => (
                      <div key={task.taskId} className="bg-white p-3 rounded border">
                        <div className="flex justify-between items-start mb-2">
                          <span className="text-sm font-medium">Задача {index + 1}</span>
                          <span className="text-xs text-gray-500 font-mono">{task.taskId}</span>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          <div>
                            <span className="text-gray-500">Запросов:</span>
                            <span className="ml-1 font-medium">{task.totalRequests}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Токенов:</span>
                            <span className="ml-1 font-medium">{formatTokens(task.totalTokens)}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Стоимость:</span>
                            <span className="ml-1 font-medium">{formatCost(task.totalCost)}</span>
                          </div>
                        </div>
                        {task.requests.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-gray-100">
                            <div className="text-xs text-gray-500 mb-1">Операции:</div>
                            <div className="space-y-1">
                              {task.requests.map((request, reqIndex) => (
                                <div key={reqIndex} className="bg-gray-50 px-2 py-1 rounded">
                                  <div className="flex justify-between items-center mb-1">
                                    <span className="font-medium capitalize">{request.operation.replace(/_/g, ' ')}</span>
                                    <span className="font-medium text-gray-600">{formatCost(request.costData.estimatedCost.totalCost)}</span>
                                  </div>
                                  <div className="flex gap-3 text-xs text-gray-500">
                                    <span>Всего: {formatTokens(request.costData.totalTokens)}</span>
                                    <span className="text-blue-600">Вход: {formatTokens(request.costData.promptTokens)}</span>
                                    <span className="text-green-600">Выход: {formatTokens(request.costData.completionTokens)}</span>
                                  </div>
                                  <div className="flex gap-3 text-xs text-gray-500 mt-1">
                                    <span>Стоимость: {formatCost(request.costData.estimatedCost.totalCost)}</span>
                                    <span className="text-blue-600">Вход: {formatCost(request.costData.estimatedCost.inputCost)}</span>
                                    <span className="text-green-600">Выход: {formatCost(request.costData.estimatedCost.outputCost)}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

